import type { MessageDescriptor } from 'react-intl-next';

import PanelNoteIcon from '@atlaskit/icon/core/discovery';
import PanelErrorIcon from '@atlaskit/icon/core/error';
import PanelInfoIcon from '@atlaskit/icon/core/information';
import TableIcon from '@atlaskit/icon/core/spreadsheet';
import PanelSuccessIcon from '@atlaskit/icon/core/success';
import PanelWarnIcon from '@atlaskit/icon/core/warning';

import SuggestedEditIcon from '../../context-panel/setting/assets/SuggestedEditIcon';
import SuggestedPanelIcon from '../../context-panel/setting/assets/SuggestedPanelIcon';
import TextBreakIcon from '../../context-panel/setting/assets/TextBreakIcon';

import type { TransformAction, Recommendation } from './api';
import messages from './messages';
import type { SettingDisplayType } from './states';

/**
 * Type for the suggestion setting item
 */
export type SuggestionItemType = {
	Icon: React.ElementType;
	settingKey: keyof SettingDisplayType;
	title: MessageDescriptor;
	description: MessageDescriptor;
	defaultValue?: boolean;
	parentSetting?: SettingKeys;
	transformationType?: TransformAction;
	display?: boolean;
	indent?: boolean;
};

/**
 * Setting keys for the suggestion settings
 */
export enum SettingKeys {
	ALL_SUGGESTIONS = 'displayAllSuggestions',
	ALL_PANELS = 'displayAllPanelSuggestions',
	CONVERT_TO_ERROR_PANEL = 'displayErrorPanel',
	CONVERT_TO_INFO_PANEL = 'displayInfoPanel',
	CONVERT_TO_NOTE_PANEL = 'displayNotePanel',
	CONVERT_TO_SUCCESS_PANEL = 'displaySuccessPanel',
	CONVERT_TO_WARN_PANEL = 'displayWarningPanel',
	CONVERT_TO_TABLE = 'displayTable',
	SPLIT_PARAGRAPH = 'displaySplitParagraph',
}

/**
 * Array of suggestion setting items
 */
export const suggestionSettingArray: SuggestionItemType[] = [
	{
		Icon: SuggestedEditIcon,
		settingKey: SettingKeys.ALL_SUGGESTIONS,
		title: messages.toggleAllSuggestionsTitle,
		description: messages.toggleAllSuggestionsDescription,
	},
	{
		Icon: SuggestedPanelIcon,
		settingKey: SettingKeys.ALL_PANELS,
		title: messages.toggleAllPanelSuggestionsTitle,
		description: messages.toggleAllPanelSuggestionsDescription,
		parentSetting: SettingKeys.ALL_SUGGESTIONS,
	},
	{
		Icon: PanelErrorIcon,
		settingKey: SettingKeys.CONVERT_TO_ERROR_PANEL,
		title: messages.toggleErrorPanelTitle,
		description: messages.toggleErrorPanelDescription,
		parentSetting: SettingKeys.ALL_PANELS,
		transformationType: 'CONVERT_TO_ERROR_PANEL',
	},
	{
		Icon: PanelInfoIcon,
		settingKey: SettingKeys.CONVERT_TO_INFO_PANEL,
		title: messages.toggleInfoPanelTitle,
		description: messages.toggleInfoPanelDescription,
		parentSetting: SettingKeys.ALL_PANELS,
		transformationType: 'CONVERT_TO_INFO_PANEL',
	},
	{
		Icon: PanelNoteIcon,
		settingKey: SettingKeys.CONVERT_TO_NOTE_PANEL,
		title: messages.toggleNotePanelTitle,
		description: messages.toggleNotePanelDescription,
		parentSetting: SettingKeys.ALL_PANELS,
		transformationType: 'CONVERT_TO_NOTE_PANEL',
	},
	{
		Icon: PanelSuccessIcon,
		settingKey: SettingKeys.CONVERT_TO_SUCCESS_PANEL,
		title: messages.toggleSuccessPanelTitle,
		description: messages.toggleSuccessPanelDescription,
		parentSetting: SettingKeys.ALL_PANELS,
		transformationType: 'CONVERT_TO_SUCCESS_PANEL',
	},
	{
		Icon: PanelWarnIcon,
		settingKey: SettingKeys.CONVERT_TO_WARN_PANEL,
		title: messages.toggleWarningPanelTitle,
		description: messages.toggleWarningPanelDescription,
		parentSetting: SettingKeys.ALL_PANELS,
		transformationType: 'CONVERT_TO_WARN_PANEL',
	},
	{
		Icon: TextBreakIcon,
		settingKey: SettingKeys.SPLIT_PARAGRAPH,
		title: messages.toggleSplitParagraphTitle,
		description: messages.toggleSplitParagraphDescription,
		parentSetting: SettingKeys.ALL_SUGGESTIONS,
		transformationType: 'SPLIT_PARAGRAPH',
	},
	{
		Icon: TableIcon,
		settingKey: SettingKeys.CONVERT_TO_TABLE,
		title: messages.toggleTableTitle,
		description: messages.toggleTableDescription,
		parentSetting: SettingKeys.ALL_SUGGESTIONS,
		transformationType: 'CONVERT_TO_TABLE',
	},
];

const isItemVisible = (item: SuggestionItemType, result: SuggestionItemType[]): boolean => {
	const isAllSuggestions = item.settingKey === SettingKeys.ALL_SUGGESTIONS;
	const isParentSettingEnabled = item.parentSetting
		? result.find((i) => i.settingKey === item.parentSetting)?.defaultValue ?? false
		: false;
	return isAllSuggestions || isParentSettingEnabled;
};

/**
 * Get the suggestion settings with their
 * default values
 * display and indent properties
 * in a correct order
 */
export const getSettingsWithValues = (settings: SettingDisplayType | null) => {
	const result: SuggestionItemType[] = [];
	const processed = new Set<keyof SettingDisplayType>();

	const processSetting = (setting: SuggestionItemType) => {
		if (processed.has(setting.settingKey)) {
			return;
		}

		if (setting.parentSetting && !processed.has(setting.parentSetting)) {
			const parentSetting = suggestionSettingArray.find(
				(s) => s.settingKey === setting.parentSetting,
			);
			if (parentSetting) {
				processSetting(parentSetting);
			}
		}

		processed.add(setting.settingKey);
		result.push({
			...setting,
			defaultValue: settings?.[setting.settingKey] ?? true,
			display: isItemVisible(setting, result),
			settingKey: setting.settingKey,
			indent:
				setting.parentSetting && setting.parentSetting !== SettingKeys.ALL_SUGGESTIONS
					? true
					: false,
		});
	};

	suggestionSettingArray.forEach(processSetting);

	return result;
};

/**
 * Type for the return setting object
 */
export type returnSettingType = {
	[key: string]: boolean;
};

/**
 * Get the new settings based on the setting key
 */
export const getNewSettings = (
	settings: SettingDisplayType | null,
	settingKey: keyof SettingDisplayType | null,
	enableAllSuggestions: boolean,
) => {
	const suggestionSettingWithValues = getSettingsWithValues(settings);

	const newSettings = suggestionSettingWithValues.reduce<returnSettingType>((suggestion, s) => {
		suggestion[s.settingKey] = s.defaultValue!;
		return suggestion;
	}, {});

	if (settingKey && !enableAllSuggestions) {
		newSettings[settingKey] = !newSettings[settingKey];

		// toggle all child settings recursively
		const toggleChildSettings = (parentKey: keyof SettingDisplayType, newSettingValue: boolean) => {
			suggestionSettingWithValues.forEach((value) => {
				if (value.parentSetting === parentKey) {
					newSettings[value.settingKey] = newSettingValue;
					toggleChildSettings(value.settingKey, newSettingValue);
				}
			});
		};

		toggleChildSettings(settingKey, newSettings[settingKey]);
	}

	// toggle all suggestions to true from button click
	if (enableAllSuggestions) {
		(Object.keys(newSettings) as Array<keyof SettingDisplayType>).forEach((key) => {
			newSettings[key] = true;
		});
	}

	return newSettings;
};

/**
 * Sync recommendations based on the settings
 */
export const syncFilteredRecommendations = (
	recommendations: Recommendation[],
	settings: SettingDisplayType,
): Recommendation[] => {
	const suggestionSettingWithValues = getSettingsWithValues(settings);
	const newSettings = suggestionSettingWithValues.reduce<returnSettingType>((suggestion, s) => {
		suggestion[s.transformationType!] = s.defaultValue!;
		return suggestion;
	}, {});

	return recommendations.map((recommendation) => {
		return {
			...recommendation,
			filtered: newSettings[recommendation.transformAction] === false,
		};
	});
};

/**
 * Get the transformation with icon
 */
export const getTransformActionIconMap = (): Record<TransformAction, React.ElementType> => {
	return suggestionSettingArray.reduce(
		(acc, item) => {
			if (item.transformationType) {
				acc[item.transformationType] = item.Icon;
			}
			return acc;
		},
		{} as Record<TransformAction, React.ElementType>,
	);
};
